import {
  ContactType,
  CreateEmployerContactDto,
  EmployerContactDto,
} from '@zorro/clients';
import { convertToYesNo, isDefined } from '@zorro/common-utils';
import { formatPhone } from '@zorro/web/ui-utils';
import { ReactElement } from 'react';
import * as yup from 'yup';

import { ReviewItemProps } from '../AddEmployerFlow/ReviewForm/ReviewCard';
import { PersonFormItem, getPersonFormSchema } from '../PersonFormArray';

export const getContactsSchemaObject = (
  isFinalizationMode: boolean,
  isDon: boolean
) => {
  return yup.object({
    legalContacts: yup
      .array()
      .of(getPersonFormSchema(isFinalizationMode))
      .required(),
    hrContacts: yup
      .array()
      .of(getPersonFormSchema(isFinalizationMode))
      .required(),
    financeContacts: yup
      .array()
      .of(getPersonFormSchema(isFinalizationMode))
      .required(),
    onboardingContacts: yup
      .array()
      .of(getPersonFormSchema(isFinalizationMode && isDon))
      .required(),
  });
};

export type ContactsFormFields = yup.InferType<
  ReturnType<typeof getContactsSchemaObject>
>;

const contactTypeToContactFieldNameConfig: Record<
  ContactType,
  keyof ContactsFormFields
> = {
  [ContactType.ONBOARDING]: 'onboardingContacts',
  [ContactType.LEGAL]: 'legalContacts',
  [ContactType.HR]: 'hrContacts',
  [ContactType.FINANCE]: 'financeContacts',
};

export const mapContactItemToEmployerContactDto = (
  formFields: ContactsFormFields,
  type: ContactType,
  duplicateFieldName: keyof ContactsFormFields
): CreateEmployerContactDto[] => {
  const fieldName = contactTypeToContactFieldNameConfig[type];
  const isDuplicateMode = formFields[fieldName][0].isDuplicateMode;
  const contacts = isDuplicateMode
    ? formFields[duplicateFieldName]
    : formFields[fieldName];

  return contacts
    .filter(({ firstName, lastName, email, phone }) => {
      if (isDuplicateMode) {
        return true;
      }
      return firstName || lastName || email || phone;
    })
    .map(({ firstName, lastName, email, phone, isAdmin = false }) => ({
      isAdmin,
      firstName: firstName || null,
      lastName: lastName || null,
      email: email || null,
      phone: phone || null,
      type,
    }));
};

export function mapEmployerContactDtoToPersonFormItem(
  contacts: EmployerContactDto[]
): PersonFormItem[] {
  return contacts.map(({ firstName, lastName, phone, email, isAdmin }) => ({
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    phone: phone ?? '',
    email: email ?? '',
    isAdmin,
    isDuplicateMode: false,
  }));
}

export const mapContactItemsToReviewItems = (
  items: PersonFormItem[],
  contactTitle: string,
  icon: ReactElement,
  isFinalizationMode: boolean
): ReviewItemProps[] => {
  const reviewItems: ReviewItemProps[] = items.flatMap(
    ({ firstName, lastName, phone, email, isAdmin }) => [
      {
        name: `${contactTitle} contact full name`,
        value: firstName && lastName && `${firstName} ${lastName}`,
        isRequired: isFinalizationMode,
        icon,
      },
      {
        name: 'Phone number',
        value: phone ? formatPhone(phone) : undefined,
        isRequired: isFinalizationMode,
      },
      {
        name: 'Email address',
        value: email,
        isRequired: isFinalizationMode,
      },
      {
        name: 'Is admin?',
        value: isDefined(isAdmin) && convertToYesNo(isAdmin),
        isRequired: isFinalizationMode,
        span: 12,
      },
    ]
  );
  reviewItems.push({
    name: 'separator',
    isSeparator: true,
  });

  return reviewItems;
};
